import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { formatIQD, todayISO } from "../lib/format";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Download, Check } from "lucide-react";
import { exportToExcel } from "../lib/excel";

export default function SalaryDeductions() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState({});
  const [date, setDate] = useState(todayISO());
  const load = () => api.get("/balances", { params: { only_outstanding: true } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const toggle = (id) => setSelected({ ...selected, [id]: !selected[id] });
  const chosen = rows.filter((r) => selected[r.id]);
  const total = chosen.reduce((s, r) => s + r.outstanding, 0);

  const exportReport = () => {
    if (chosen.length === 0) return toast.error("Select at least one employee");
    exportToExcel(chosen.map((r) => ({
      Name: r.name, Phone: r.phone || "", Email: r.email || "", Deduction: r.outstanding, Date: date,
    })), "Salary Deductions", `salary-deductions-${date}`);
    toast.success("Report exported");
  };

  const process = async () => {
    if (chosen.length === 0) return toast.error("Select at least one employee");
    const items = chosen.map((r) => ({ employee_id: r.id, amount: r.outstanding }));
    try {
      await api.post("/payments/bulk", { payment_date: date, payment_method: "Salary Deduction", items, notes: "Salary deduction batch" });
      toast.success(`Processed ${chosen.length} salary deductions totaling ${formatIQD(total)}`);
      setSelected({}); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="deductions-page">
      <div>
        <h1 className="heading text-2xl font-extrabold tracking-tight">Salary Deductions</h1>
        <p className="text-sm text-muted-foreground">Select employees whose outstanding balances should be deducted from salary. Generate a report and record as payment when processed.</p>
      </div>
      <Card className="p-4">
        <div className="flex gap-3 items-end flex-wrap">
          <div><Label>Deduction Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" data-testid="ded-date" /></div>
          <Button variant="outline" onClick={exportReport} data-testid="btn-export-ded"><Download size={14} className="mr-1" />Export Report</Button>
          <Button onClick={process} data-testid="btn-process-ded" className="bg-emerald-600 hover:bg-emerald-700"><Check size={14} className="mr-1" />Confirm & Record as Payments</Button>
        </div>
      </Card>
      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50"><tr className="text-left text-xs uppercase text-muted-foreground"><th className="p-3"></th><th className="p-3">Employee</th><th className="p-3">Phone</th><th className="p-3">Email</th><th className="p-3 text-right">Outstanding</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t table-row-hover" data-testid={`ded-row-${r.id}`}>
                <td className="p-3"><Checkbox checked={!!selected[r.id]} onCheckedChange={() => toggle(r.id)} data-testid={`ded-check-${r.id}`} /></td>
                <td className="p-3 font-medium">{r.name}</td>
                <td className="p-3 mono text-muted-foreground">{r.phone || "—"}</td>
                <td className="p-3 text-muted-foreground">{r.email || "—"}</td>
                <td className="p-3 text-right mono font-semibold text-rose-700">{formatIQD(r.outstanding)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No outstanding balances.</td></tr>}
          </tbody>
        </table>
        <div className="p-4 border-t bg-slate-900 text-white flex justify-between items-center flex-wrap gap-3">
          <div className="flex gap-6">
            <div><div className="text-xs uppercase text-slate-400">Selected</div><div className="heading text-xl font-bold">{chosen.length}</div></div>
            <div><div className="text-xs uppercase text-emerald-300">Total Deduction</div><div className="heading text-xl font-bold text-emerald-300">{formatIQD(total)}</div></div>
          </div>
        </div>
      </Card>
    </div>
  );
}
